import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { config } from "../config.js";
import { createPlayerPod, createEnemyPods, createBossShip, createLargePod } from "./pods.js";
import { createEffects } from "./effects.js";
import { createDepthCues } from "./depth.js";
import { createOverlay } from "./overlay.js";
import { createStageObjects, OWN_MESH_TYPES, POD_LOOKS } from "./stage-objects.js";
import { createCannonView } from "./cannon-view.js";
import { createGraphicsQuality } from "./graphics-quality.js";
import { createStages } from "./stages.js";
import { cameraFor, CAMERA_MODES } from "./camera-modes.js";
import { createReplayDirector, appearScale } from "./replay-camera.js";

// 描画。world を見て描くだけで、world へ書き戻さない。
// 視点はパンツァードラグーン風: 自機の後ろから、自動で前へ進んで見える(背景の星・惑星が流れる)。
// スケール感(src/sim/scale.js): ステージが進むほどカメラが引いて広角になり、敵は e.size の大きさで描く。

export function createView(canvas) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
  renderer.info.autoReset = false; // 描く回数・三角形の数を1フレームぶん(にじみの何回かの描画も合わせて)数える。render() の頭で reset する。?stats 用
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x02030a);
  const camera = new THREE.PerspectiveCamera(62, window.innerWidth / window.innerHeight, 0.5, 9000);

  scene.add(new THREE.AmbientLight(0x6070a0, 0.7));
  const sun = new THREE.DirectionalLight(0xfff2dd, 2.6);
  sun.position.set(300, 200, 120);
  scene.add(sun);
  scene.add(new THREE.HemisphereLight(0x5577ff, 0x110800, 0.5));

  // --- 背景: 流れる星、地球と月 ---
  const starMax = 600;
  const starPos = new Float32Array(starMax * 6);
  const starGeo = new THREE.BufferGeometry();
  starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3).setUsage(THREE.DynamicDrawUsage));
  const stars = new THREE.LineSegments(starGeo, new THREE.LineBasicMaterial({ color: 0xbfd6ff, transparent: true, opacity: 0.85 }));
  stars.frustumCulled = false;
  scene.add(stars);
  const starSeeds = [];
  for (let i = 0; i < starMax; i++) starSeeds.push(resetStar({}, true));
  function resetStar(s, anywhere) {
    const a = Math.random() * Math.PI * 2;
    const r = 60 + Math.random() * 900;
    s.x = Math.cos(a) * r;
    s.y = Math.sin(a) * r * 0.7;
    s.z = anywhere ? -3000 + Math.random() * 3100 : -3000;
    return s;
  }
  let starCount = starMax;

  const texLoader = new THREE.TextureLoader();
  const earthTex = texLoader.load("assets/textures/earth_2048.jpg");
  earthTex.colorSpace = THREE.SRGBColorSpace;
  const earth = new THREE.Mesh(new THREE.SphereGeometry(900, 64, 40), new THREE.MeshStandardMaterial({ map: earthTex, roughness: 0.9 }));
  earth.position.set(-4600, -3000, -6500);
  scene.add(earth);
  const moonTex = texLoader.load("assets/textures/moon_2048.jpg");
  moonTex.colorSpace = THREE.SRGBColorSpace;
  const moon = new THREE.Mesh(new THREE.SphereGeometry(160, 40, 24), new THREE.MeshStandardMaterial({ map: moonTex, roughness: 1 }));
  moon.position.set(2600, 1500, -7000);
  scene.add(moon);
  const stages = createStages(scene, { earth, moon, stars });

  // --- 機体 ---
  const player = createPlayerPod();
  scene.add(player);
  const maxEnemies = config.enemies.maxAlive.pc + 10;
  const enemyPods = createEnemyPods(maxEnemies);
  scene.add(enemyPods.body, enemyPods.thruster);
  // 大きな敵(母艦・小母艦・大玉)は1機ずつ専用の形で描く。scale: 形の大きさ1に対する倍率(e.size に掛ける)
  const T = config.enemies.types;
  const bigs = {
    boss: { group: createBossShip(), scale: 1 },
    miniboss: { group: createBossShip(), scale: T.miniboss.radius / T.boss.radius },
    bigpod: { group: createLargePod(), scale: T.bigpod.radius },
  };
  for (const b of Object.values(bigs)) {
    b.group.visible = false;
    scene.add(b.group);
  }

  // 先に1回描いて、大きな敵(母艦など)の形とシェーダーを GPU に載せておく(2026-11-01 スマホの計測で「母艦が来る!」の瞬間に 183ms の止まりが出たため)。
  // three.js は初めて映る物の形の転送とシェーダーの用意をそのとき行うので、見えない所(カメラの後ろ、視野の判定も切る)に置いて1回描き、元に戻す。
  // 起動時(タイトルの前)に main.js が呼ぶ。試合の計算には触らない
  function warmUp() {
    const list = Object.values(bigs).map((b) => b.group);
    const saved = [];
    for (const g of list) {
      g.visible = true;
      g.position.set(0, 0, camera.position.z + 500);
      g.traverse((o) => {
        if (o.isMesh || o.isPoints || o.isLine) {
          saved.push([o, o.frustumCulled]);
          o.frustumCulled = false;
        }
      });
    }
    renderer.render(scene, camera);
    for (const [o, fc] of saved) o.frustumCulled = fc;
    for (const g of list) g.visible = false;
  }

  const effects = createEffects(scene);
  const depth = createDepthCues(scene);
  const overlay = createOverlay();
  const stageObjects = createStageObjects(scene);
  const cannons = createCannonView(scene);

  // --- 光のにじみ(bloom)。軽い描画レベルでは使わない ---
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const glare = config.graphics.glare;
  const bloom = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), glare.bloom, 0.4, 0.75);
  composer.addPass(bloom);
  composer.addPass(new OutputPass());
  let useBloom = true;

  let quality = null;
  quality = createGraphicsQuality({
    renderer,
    onChange: () => {
      composer.setPixelRatio(renderer.getPixelRatio());
      effects.setPixelRatio(renderer.getPixelRatio());
      if (quality) applyEffectLevel(); // 作っている最中(最初の apply)はまだ quality が無い
    },
  });
  function effectLevel() {
    const info = quality.info();
    if (info.mode !== "auto") return config.graphics.levels[info.mode];
    if (info.lite) return config.graphics.levels.low;
    return info.level >= 2 ? config.graphics.levels.mid : config.graphics.levels.high;
  }
  function applyEffectLevel() {
    const lv = effectLevel();
    effects.setParticleScale(lv.particleScale);
    useBloom = lv.bloom;
    starCount = lv.stars;
    stages.setPropScale(lv.props);
    starGeo.setDrawRange(0, starCount * 2);
  }
  applyEffectLevel();

  let cameraMode = CAMERA_MODES[0];
  let baseFov = 62; // 倍率1のときの視野角(画面の向きで変わる)
  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / h;
    // 縦長の画面(スマホ縦持ち)では、横が見切れないよう広めに写す
    baseFov = w < h ? 80 : 62;
    camera.fov = baseFov;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
  }
  window.addEventListener("resize", resize);
  resize();

  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const v = new THREE.Vector3();
  const sc = new THREE.Vector3();
  const fwd = new THREE.Vector3(0, 0, -1);
  const flashUntil = new Map(); // 敵のid → 白く光らせる終わりの時刻(被弾したとき)
  const lastHp = new Map();
  let shake = 0;
  let flash = 0; // 画面全体の白い光(大爆発)
  const flashEl = document.getElementById("flash");

  const director = createReplayDirector();

  function onEvents(events, world) {
    effects.onEvents(events, world);
    director.onEvents(events, world);
    overlay.onEvents(events);
    for (const e of events) {
      if (e.k === "hurt") shake = Math.max(shake, 1.2);
      else if (e.k === "kill" && e.big) {
        // クリアの後の大爆発(src/view/finale.js): mega は一番大きな爆発。chain(まわりの連鎖)は白い光を弱く(続けて真っ白にならないように)
        shake = Math.max(shake, e.mega ? 7 : 3);
        flash = Math.max(flash, e.chain ? 0.35 : 1);
      } else if (e.k === "mainGun") shake = Math.max(shake, 0.6);
      else if (e.k === "cannonFire") shake = Math.max(shake, 1);
    }
  }

  // replayCamera: 試合の後のリプレイを演出カメラで見るときの視点(src/view/replay-camera.js)。ふだんは null
  function render(world, dtReal, timeScale, replayCamera = null) {
    const now = performance.now();
    renderer.info.reset();
    const p = world.player;
    const wide = camera.aspect >= 1;
    const cam = replayCamera ? director.cameraAt(world, replayCamera, baseFov, wide) : cameraFor(world, cameraMode, baseFov, wide);
    // 演出カメラでは、敵が出る・消える所も映るので、ポンと出し消しせずに大きさを育てる・縮める
    const grow = replayCamera ? (e) => appearScale(e, world) : () => 1;
    // 自機: 動く向きに傾ける(主観の視点では描かない)
    player.position.set(p.x, p.y, 0);
    player.rotation.set(-p.vy * 0.006, 0, -p.vx * 0.012);
    player.visible = !cam.hidePlayer && !(p.invuln > 0 && Math.floor(now / 70) % 2 === 0);
    for (const g of player.userData.glows) g.scale.setScalar(4.5 + Math.random() * 1.5);

    // 敵のポッド(大きな敵は別)
    let n = 0;
    const bigShown = {};
    for (const e of world.enemies) {
      if (bigs[e.type]) {
        bigShown[e.type] = e;
        continue;
      }
      const prev = lastHp.get(e.id);
      if (prev !== undefined && e.hp < prev) flashUntil.set(e.id, now + 70);
      lastHp.set(e.id, e.hp);
      if (OWN_MESH_TYPES.has(e.type)) continue; // 機雷・補給衛星・岩は stage-objects.js で描く
      if (n >= maxEnemies) continue;
      v.set(e.vx, e.vy, e.vz);
      if (v.lengthSq() < 1) v.set(0, 0, 1);
      q.setFromUnitVectors(fwd, v.normalize());
      const look = POD_LOOKS[e.type] || POD_LOOKS.pod;
      sc.setScalar(look.scale * e.size * grow(e));
      m4.compose(v.set(e.x, e.y, e.z), q, sc);
      enemyPods.body.setMatrixAt(n, m4);
      enemyPods.thruster.setMatrixAt(n, m4);
      const hit = (flashUntil.get(e.id) || 0) > now;
      const c = look.color;
      if (hit) enemyPods.body.instanceColor.setXYZ(n, 4, 4, 4);
      else enemyPods.body.instanceColor.setXYZ(n, c[0], c[1], c[2]);
      n++;
    }
    enemyPods.body.count = enemyPods.thruster.count = n;
    enemyPods.setTime(now / 1000);
    enemyPods.body.instanceMatrix.needsUpdate = enemyPods.thruster.instanceMatrix.needsUpdate = true;
    enemyPods.body.instanceColor.needsUpdate = true;
    if (lastHp.size > maxEnemies * 4) {
      const alive = new Set(world.enemies.map((e) => e.id));
      for (const id of lastHp.keys()) if (!alive.has(id)) lastHp.delete(id), flashUntil.delete(id);
    }

    for (const [type, b] of Object.entries(bigs)) {
      const e = bigShown[type];
      b.group.visible = !!e;
      if (!e) continue;
      b.group.position.set(e.x, e.y, e.z);
      b.group.scale.setScalar(e.size * b.scale * Math.max(0.001, grow(e)));
      if (type === "bigpod") b.group.rotation.set(0, Math.sin(now / 2200) * 0.25, 0);
      else b.group.rotation.z = Math.sin(now / 1500) * 0.05;
      const hit = (flashUntil.get(e.id) || 0) > now;
      const prev = lastHp.get(e.id);
      if (prev !== undefined && e.hp < prev) flashUntil.set(e.id, now + 50);
      lastHp.set(e.id, e.hp);
      for (const m of b.group.userData.mats) m.emissive.setRGB(hit ? 0.5 : 0, hit ? 0.2 : 0, hit ? 0.1 : 0);
    }

    effects.update(world, dtReal, timeScale);
    stageObjects.update(world, now, (e) => (flashUntil.get(e.id) || 0) > now, grow);
    cannons.update(world, now);
    depth.update(world, dtReal, timeScale);

    // 星を流す(前へ進んで見える)
    const flow = config.field.scrollSpeed * 6 * timeScale * world.scroll * dtReal;
    const streak = 0.5 + config.field.scrollSpeed * 0.12 * timeScale * world.scroll;
    for (let i = 0; i < starCount; i++) {
      const s = starSeeds[i];
      s.z += flow;
      if (s.z > 80) resetStar(s, false);
      starPos.set([s.x, s.y, s.z, s.x, s.y, s.z - streak], i * 6);
    }
    starGeo.attributes.position.needsUpdate = true;
    earth.rotation.y += dtReal * 0.01 * timeScale;
    const entered = stages.update(world.progress, dtReal, timeScale * world.scroll);
    if (entered && onStage) onStage(entered);

    // カメラ: 視点ごとの位置(camera-modes.js)。上下左右には少し遅れて付いていく。揺れは見た目だけ
    shake = Math.max(0, shake - dtReal * 4);
    const sk = cam.hidePlayer ? 0.4 : world.scale; // 主観では揺れを小さく(酔わないように)
    const sx = (Math.random() - 0.5) * shake * 2 * sk;
    const sy = (Math.random() - 0.5) * shake * 2 * sk;
    if (Math.abs(camera.fov - cam.fov) > 0.01) {
      camera.fov = cam.fov;
      camera.updateProjectionMatrix();
    }
    camera.position.set(cam.x + sx, cam.y + sy, cam.z);
    camera.lookAt(cam.lx, cam.ly, cam.lz);
    effects.faceCamera(camera);
    stageObjects.faceCamera(camera);
    camera.updateMatrixWorld();
    const tOverlay = performance.now();
    overlay.draw(world, camera, dtReal, cam.hidePlayer);
    const tGl = performance.now();

    flash = Math.max(0, flash - dtReal * 1.5);
    if (flashEl) flashEl.style.opacity = String(flash * 0.8);

    // 後半(倍率が大きいほど)はにじみを弱める(2026-10-30 オーナーの感想「後半やや光が強すぎてまぶしい」)
    bloom.strength = glare.bloom * Math.pow(world.scale || 1, -glare.bloomFade);
    if (useBloom) composer.render();
    else renderer.render(scene, camera);
    // 画面写真(snapshot): 描いた直後なら、WebGL の絵をそのまま写せる(preserveDrawingBuffer を使わず、ふだんの描画を重くしない)
    if (snapshotWaiters.length) {
      const layers = [renderer.domElement, overlay.canvas];
      for (const fn of snapshotWaiters.splice(0)) fn(layers);
    }
    // 描画の内訳(?stats 用。ms): scene=敵・演出・星の配置(JS)、overlay=2D の重ね描き(ダメージの数字など)、gl=GPU へ送る(three.js の render)
    const tEnd = performance.now();
    times.scene = tOverlay - now;
    times.overlay = tGl - tOverlay;
    times.gl = tEnd - tGl;
    quality.sample(dtReal, true);
  }
  const times = { scene: 0, overlay: 0, gl: 0 };
  const snapshotWaiters = [];

  let onStage = null;
  function clear() {
    effects.clear();
    overlay.clear();
    lastHp.clear();
    flashUntil.clear();
    shake = 0;
    flash = 0;
    director.clear();
  }

  return {
    render,
    onEvents,
    // 次に描いた直後の画面を受け取る(重ねる canvas の配列。下から 3D・2D)。src/ui/snapshot.js で1枚にする
    snapshot: () => new Promise((resolve) => snapshotWaiters.push(resolve)),
    clear,
    quality,
    warmUp,
    // 確認用の負荷の表示(src/ui/stats.js)に渡す数: 描画の細かさ(quality.info())と、実際に描く画素・描く回数・三角形の数
    stats() {
      const r = renderer.info.render;
      return { ...quality.info(), width: canvas.width, height: canvas.height, calls: r.calls, triangles: r.triangles, times: { ...times } };
    },
    // ステージが変わったときに呼ぶ関数(見出しとミラの一言。main.js)
    onStageChange(fn) {
      onStage = fn;
    },
    resetStage() {
      stages.reset();
    },
    // 視点(config.graphics.cameras.order のどれか)
    get cameraMode() {
      return cameraMode;
    },
    setCameraMode(m) {
      if (CAMERA_MODES.includes(m)) cameraMode = m;
    },
  };
}
